"use client";

import { useCallback, useEffect, useState } from "react";
import { TweetSkeleton } from "react-tweet";
import type { Tweet } from "react-tweet/api";
import { CustomEmbeddedTweetClient } from "@/components/tweets/custom-embedded-tweet-client";
import { tweetsApiResponseSchema } from "@/components/tweets/tweets-api-schema";

type TweetLoadState = "idle" | "loading" | "loaded" | "error";

export function TwitterNoteSourceCard({ sourceId }: { sourceId: string }) {
  const [tweetLoadState, setTweetLoadState] = useState<TweetLoadState>("idle");
  const [tweet, setTweet] = useState<Tweet | null>(null);

  const fetchTweet = useCallback(async () => {
    setTweetLoadState("loading");
    try {
      const response = await fetch(
        `/api/tweets?ids=${encodeURIComponent(sourceId)}`
      );
      const json: unknown = await response.json();
      const parsed = tweetsApiResponseSchema.safeParse(json);
      if (!(response.ok && parsed.success)) {
        setTweetLoadState("error");
        return;
      }
      const first = parsed.data.tweets[0];
      setTweet(first?.tweet ?? null);
      setTweetLoadState("loaded");
    } catch {
      setTweetLoadState("error");
    }
  }, [sourceId]);

  useEffect(() => {
    setTweetLoadState("idle");
    setTweet(null);
    fetchTweet();
  }, [fetchTweet]);

  return (
    <div className="[&_.react-tweet-theme]:!bg-transparent [&_.react-tweet-theme]:!m-0">
      {tweetLoadState === "loading" && <TweetSkeleton />}
      {tweetLoadState === "error" && (
        <p className="text-[0.85rem] text-[var(--explore-text-secondary)]">
          Could not load tweet.
        </p>
      )}
      {tweetLoadState === "loaded" && tweet && (
        <CustomEmbeddedTweetClient tweet={tweet} />
      )}
      {tweetLoadState === "loaded" && !tweet && (
        <p className="text-[0.85rem] text-[var(--explore-text-secondary)]">
          Tweet unavailable.
        </p>
      )}
    </div>
  );
}
